import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useClientFilter } from '@/hooks/useClientFilter';

interface LicenseUsage {
  current_technicians: number;
  current_users: number;
  max_technicians: number;
  max_users: number | null;
  license_status: string;
  client_name: string;
}

interface LicenseValidationResult {
  isValid: boolean;
  message?: string;
  usage?: LicenseUsage;
}

export const useLicenseValidation = () => {
  const [licenseUsage, setLicenseUsage] = useState<LicenseUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { currentClientId, isAdmin } = useClientFilter();

  const fetchLicenseUsage = async (clientId?: string) => {
    try {
      setLoading(true);
      const targetClientId = clientId || currentClientId;
      
      if (!targetClientId) {
        setLicenseUsage(null);
        setLoading(false);
        return;
      }

      // Buscar informações da licença do cliente
      const { data: license, error: licenseError } = await supabase
        .from('client_licenses')
        .select(`
          current_technicians,
          current_users,
          status,
          license_plan:license_plans (
            max_technicians,
            max_users,
            name
          ),
          client:clients (
            name
          )
        `)
        .eq('client_id', targetClientId)
        .eq('status', 'active')
        .maybeSingle();

      if (licenseError || !license) {
        console.error('Error fetching license:', licenseError);
        setLicenseUsage(null);
        setLoading(false);
        return;
      }

      // Contar usuários atuais por tipo
      const { data: userCounts, error: countError } = await supabase
        .from('users')
        .select('role')
        .eq('client_id', targetClientId)
        .eq('status', 'active');

      if (countError) {
        console.error('Error counting users:', countError);
        setLoading(false);
        return;
      }

      const techCount = userCounts?.filter(u => u.role === 'technician').length || 0;
      const userCount = userCounts?.filter(u => ['user', 'client_admin'].includes(u.role)).length || 0;

      const usage: LicenseUsage = {
        current_technicians: techCount,
        current_users: userCount,
        max_technicians: license.license_plan?.max_technicians || 0,
        max_users: license.license_plan?.max_users,
        license_status: license.status,
        client_name: license.client?.name || 'Cliente'
      };

      setLicenseUsage(usage);
    } catch (error) {
      console.error('Error in fetchLicenseUsage:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateUserAddition = async (role: string, clientId?: string): Promise<LicenseValidationResult> => {
    try {
      const targetClientId = clientId || currentClientId;
      if (!targetClientId) {
        return { isValid: false, message: "Cliente não identificado" };
      }

      await fetchLicenseUsage(targetClientId);
      
      if (!licenseUsage) {
        return { isValid: false, message: "Licença não encontrada para este cliente" };
      }

      if (role === 'technician') {
        if (licenseUsage.current_technicians >= licenseUsage.max_technicians) {
          return {
            isValid: false,
            message: `Limite de técnicos atingido. Atual: ${licenseUsage.current_technicians}/${licenseUsage.max_technicians}`,
            usage: licenseUsage
          };
        }
      } else if (['user', 'client_admin'].includes(role)) {
        if (licenseUsage.max_users !== null && licenseUsage.current_users >= licenseUsage.max_users) {
          return {
            isValid: false,
            message: `Limite de usuários atingido. Atual: ${licenseUsage.current_users}/${licenseUsage.max_users}`,
            usage: licenseUsage
          };
        }
      }

      return { isValid: true, usage: licenseUsage };
    } catch (error) {
      console.error('Error in validateUserAddition:', error);
      return { isValid: false, message: "Erro ao validar limites da licença" };
    }
  };

  const checkLicenseHealth = () => {
    if (!licenseUsage) return { status: 'unknown', message: 'Licença não carregada' };

    const techUsage = licenseUsage.max_technicians > 0 
      ? (licenseUsage.current_technicians / licenseUsage.max_technicians) * 100 
      : 0;

    const userUsage = licenseUsage.max_users 
      ? (licenseUsage.current_users / licenseUsage.max_users) * 100 
      : 0;

    const maxUsage = Math.max(techUsage, userUsage);

    if (maxUsage >= 100) {
      return { status: 'critical', message: 'Limite da licença atingido' };
    } else if (maxUsage >= 80) {
      return { status: 'warning', message: 'Próximo do limite da licença' };
    } else {
      return { status: 'healthy', message: 'Licença dentro do limite' };
    }
  };

  useEffect(() => {
    if (currentClientId && !isAdmin) {
      fetchLicenseUsage();
    }
  }, [currentClientId, isAdmin]);

  return {
    licenseUsage,
    loading,
    fetchLicenseUsage,
    validateUserAddition,
    checkLicenseHealth,
    refreshUsage: () => fetchLicenseUsage()
  };
};